/**
 * Cliente delgado del OCR de tickets. La lógica real (motor + extractores) vive
 * en el backend (`POST /api/v1/tickets/ocr`) para:
 *   - centralizar credenciales (Google Cloud Vision se autentica con clave de
 *     servicio que no debe salir del servidor),
 *   - reaprovechar los mismos regex de extracción que el módulo de payroll-import,
 *   - permitir cambiar de motor sin tocar el frontend.
 *
 * Si el endpoint devuelve `enabled: false` (OCR desactivado por falta de
 * credenciales en backend), tratamos el resultado como sin sugerencias y el
 * formulario se queda vacío — el empleado rellena a mano.
 */

export type SupportedCurrency = 'EUR' | 'USD' | 'AED' | 'SAR';
export const SUPPORTED_CURRENCIES: readonly SupportedCurrency[] = ['EUR', 'USD', 'AED', 'SAR'];
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  EUR: '€',
  USD: '$',
  AED: 'AED',
  SAR: 'SAR',
};

export interface OcrResult {
  text: string;
  suggestedCategory: string | null;
  suggestedTitle: string | null;
  suggestedDate: string | null; // formato ISO yyyy-mm-dd
  suggestedAmountCents: number | null;
  suggestedCurrency: SupportedCurrency | null;
  /** false si el backend no tiene credenciales (config off) */
  enabled: boolean;
  /** true si se intentó OCR pero el motor falló en runtime (clave inválida,
   *  sin red, cuota agotada…). El front lo distingue de `!enabled` para
   *  enseñar un cartel más preciso al empleado. */
  failed: boolean;
}

export async function ocrTicket(file: File): Promise<OcrResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/v1/tickets/ocr', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    const message =
      (Array.isArray(body.message) ? body.message[0] : body.message) ?? `HTTP ${res.status}`;
    throw new Error(message as string);
  }
  return (await res.json()) as OcrResult;
}
